import React from 'react';
import { Button, Tooltip, Typography, message } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import apiClient from '../../../shared/services/apiClient';
import { t as tt } from '../../../shared/i18n';

const { Text } = Typography;

/** 对象存储(S3)控制台接口 — /api/user/s3/*。返回 body.data；失败抛 {error, status, toasted} */
export const BASE = '/api/user/s3';

const call = async (p) => {
    try {
        const res = await p;
        const body = res?.data || {};
        if (body.success === false) {
            // eslint-disable-next-line no-throw-literal
            throw { error: body.error || body.message, status: res?.status };
        }
        return body.data;
    } catch (e) {
        if (e && !e.response && 'error' in e) throw e;
        const body = e?.response?.data || {};
        // eslint-disable-next-line no-throw-literal
        throw { error: body.error || body.message || e?.message, status: e?.response?.status, toasted: !!e?.toasted };
    }
};

export const s3 = {
    get: (path, params) => call(apiClient.get(`${BASE}${path}`, params ? { params } : undefined)),
    post: (path, data) => call(apiClient.post(`${BASE}${path}`, data || {})),
    put: (path, data) => call(apiClient.put(`${BASE}${path}`, data || {})),
    del: (path) => call(apiClient.delete(`${BASE}${path}`)),
};

/** apiClient 已对多数 HTTP 错误弹过提示；只补没弹过的 */
export const toastErr = (e, fallback) => {
    if (!e?.toasted) message.error(e?.error || e?.message || fallback || tt('Operation failed'));
};

export const listOf = (d) => (Array.isArray(d) ? d : (Array.isArray(d?.items) ? d.items : []));

export const fmtBytes = (v) => {
    const n = Number(v) || 0;
    if (n <= 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
    const x = n / 1024 ** i;
    return `${i === 0 ? x : x.toFixed(x >= 100 ? 0 : x >= 10 ? 1 : 2)} ${units[i]}`;
};

export const fmtCount = (v) => {
    const n = Number(v) || 0;
    const abs = Math.abs(n);
    if (abs >= 1e9) return `${+(n / 1e9).toFixed(2)}B`;
    if (abs >= 1e6) return `${+(n / 1e6).toFixed(2)}M`;
    if (abs >= 1e4) return `${+(n / 1e3).toFixed(1)}K`;
    return n.toLocaleString();
};

export const fmtGb = (v) => `${+(Number(v) || 0).toFixed(3)} GB`;

export const fmtDate = (v) => {
    if (!v) return '-';
    const d = new Date(String(v).replace(' ', 'T'));
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString();
};

/** 桶名：3–63，小写/数字/连字符，首尾字母数字，不能 yc- 开头 */
export const BUCKET_NAME_RE = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;
export const bucketNameError = (name) => {
    const v = String(name || '');
    if (v.length < 3 || v.length > 63) return tt('3–63 characters');
    if (!BUCKET_NAME_RE.test(v)) return tt('Lowercase letters, digits and hyphens only');
    if (v.startsWith('yc-')) return tt('Cannot start with "yc-"');
    return null;
};

export const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(String(text ?? ''));
        message.success(tt('Copied'));
    } catch (_) {
        message.error(tt('Failed to copy. Please copy manually.'));
    }
};

export const CopyBtn = ({ text, size = 'small', label }) => (
    <Tooltip title={label ? undefined : tt('Copy')}>
        <Button size={size} type={label ? 'default' : 'text'} icon={<CopyOutlined />} onClick={() => copyText(text)} disabled={!text}>{label}</Button>
    </Tooltip>
);

/** 一行：等宽值 + 复制 */
export const CopyLine = ({ value, style }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, maxWidth: '100%', ...style }}>
        <Text code style={{ wordBreak: 'break-all' }}>{value || '-'}</Text>
        {value ? <CopyBtn text={value} /> : null}
    </span>
);

export const PERMISSIONS = [
    { value: 'read', label: 'Read only' },
    { value: 'readwrite', label: 'Read & write' },
    { value: 'admin', label: 'Admin' },
];
export const permLabel = (p) => tt((PERMISSIONS.find((x) => x.value === p) || {}).label || p || '-');
